import { useMutation } from '@apollo/client';
import {
    faAlarmClock,
    faArrowRight,
    faBell,
    faBellSlash,
    faCircleCheck,
    faClockRotateLeft,
    faEllipsisVertical,
    faEnvelope,
    faPause,
    faPen,
    faPlay,
    faRotateRight,
    faTrashCan,
    faTriangleExclamation,
    faUser,
} from '@fortawesome/pro-regular-svg-icons';
import * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { StatusLabel, type StatusTone } from './RunStatusBadge.tsx';
import { formatCadence, formatRunDate, validationErrorMessage } from './scheduleFormat.ts';
import { useScheduleActions } from './useScheduleActions.ts';
import Button from '../../../core/components/Button.tsx';
import ConfirmModal from '../../../core/components/ConfirmModal.tsx';
import DropdownMenu from '../../../core/components/DropdownMenu.tsx';
import { withIcon } from '../../../core/components/Icon.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';
import { useApplicationContext } from '../../../core/contexts/ApplicationContext.tsx';
import { url } from '../../../core/utils/link.ts';
import AssistantCardShell from '../../../products/assistants/components/AssistantCardShell.tsx';
import { canManageAgents } from '../../../users/utils.ts';
import { AUTOMATION_PATH, CONVERSATION_PATH } from '../../constants.ts';
import { graphqlErrorMessage } from '../../errors.ts';
import {
    DELETE_TRIGGER,
    GET_AGENT_TRIGGERS,
    GET_MY_TRIGGERS,
    SET_SHARED_SCHEDULE_MUTED,
    UPDATE_TRIGGER,
} from '../../queries.ts';
import { displayName } from '../ProjectSharingFields.tsx';

const ClockIcon = withIcon(faAlarmClock);
const MailIcon = withIcon(faEnvelope);
const SucceededIcon = withIcon(faCircleCheck);
const NeverRunIcon = withIcon(faClockRotateLeft);
const WarningIcon = withIcon(faTriangleExclamation);
const EllipsisIcon = withIcon(faEllipsisVertical);

export const itemClassName = 'gap-3 px-3 py-2.5 text-sm';
export const dangerItemClassName =
    'gap-3 px-3 py-2.5 text-sm text-content-danger-strong hover:text-content-danger-strong hover:!bg-surface-danger-weak';
export const menuIconClassName = 'w-4 flex-none text-content-medium';
export const dangerMenuIconClassName = 'w-4 flex-none';
export const EditMenuIcon = withIcon(faPen);
export const OpenChatMenuIcon = withIcon(faArrowRight);
const UserIcon = withIcon(faUser);
export const PauseMenuIcon = withIcon(faPause);
export const ResumeMenuIcon = withIcon(faPlay);
export const DeleteMenuIcon = withIcon(faTrashCan);
export const RunNowMenuIcon = withIcon(faRotateRight);
const MuteMenuIcon = withIcon(faBellSlash);
const UnmuteMenuIcon = withIcon(faBell);

type LastRun = 'succeeded' | 'failed' | null;

const lastRunLine = (t, { at, outcome }: { at?: string | null; outcome: LastRun }) => {
    if (outcome === 'failed')
        return {
            icon: <WarningIcon className="text-content-danger-default" />,
            label: (
                <span className="text-content-danger-default">
                    {at ? t('automations.lastRunFailed', { date: formatRunDate(at) }) : t('trigger.lastRunFailed')}
                </span>
            ),
        };
    if (at) return { icon: <SucceededIcon />, label: t('automations.lastRunSucceeded', { date: formatRunDate(at) }) };
    return { icon: <NeverRunIcon />, label: t('automations.neverRun') };
};

const subtitle = (status: { tone: StatusTone; label: string } | null, kind: string) =>
    status ? (
        <>
            <StatusLabel tone={status.tone}>{status.label}</StatusLabel>
            {` · ${kind}`}
        </>
    ) : (
        kind
    );

const scheduleStatus = (t, schedule: any) => {
    if (schedule.lastRunStatus === 'running') return { tone: 'agent' as const, label: t('automations.detail.running') };
    if (schedule.autoPaused) return { tone: 'danger' as const, label: t('automations.autoPaused') };
    if (!schedule.active) return { tone: 'warning' as const, label: t('automations.paused') };
    return null;
};

const scheduleLastRun = (schedule: any): LastRun => {
    if (schedule.lastRunStatus === 'running')
        return schedule.lastRunAt ? (schedule.consecutiveFailures > 0 ? 'failed' : 'succeeded') : null;
    if (schedule.lastRunStatus === 'failed') return 'failed';
    return schedule.lastRunAt ? 'succeeded' : null;
};

interface CardMenuProps {
    isDisabled: boolean;
    children: React.ReactNode;
    // accessible name of the ⋮ trigger
    label?: string;
}

/** The hover ⋮ menu in the card's header slot (automations and projects). The slot sits inside the card link, so
 *  its clicks must neither navigate nor open the card. */
export const CardMenu = ({ isDisabled, children, label }: CardMenuProps) => {
    const { t } = useTranslation('chat');
    return (
        <div
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
            }}
            onKeyDown={(e) => e.stopPropagation()}
            className="flex flex-none items-center"
        >
            <DropdownMenu>
                <DropdownMenu.Trigger asChild>
                    <Button
                        type="button"
                        variant="ghost"
                        aria-label={label ?? t('automations.menu.more')}
                        className="flex h-8 w-8 items-center justify-center px-0 py-0 opacity-0 focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-line-brand-strong group-hover:opacity-100 group-focus-visible:opacity-100 disabled:group-hover:opacity-50 data-[state=open]:opacity-100"
                        disabled={isDisabled}
                    >
                        <EllipsisIcon />
                    </Button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Content align="end">{children}</DropdownMenu.Content>
            </DropdownMenu>
        </div>
    );
};

export const DeleteConfirm = ({ name, kind, disabled, onConfirm, onCancel }) => {
    const { t } = useTranslation('chat');
    return (
        <ConfirmModal
            title={t('automations.deleteConfirm.title')}
            labelConfirm={t('automations.deleteConfirm.yes')}
            confirmDisabled={disabled}
            danger
            onConfirm={onConfirm}
            onCancel={onCancel}
        >
            {t(kind === 'email' ? 'automations.deleteConfirm.textEmail' : 'automations.deleteConfirm.text', { name })}
        </ConfirmModal>
    );
};

export const ScheduleCard = ({ schedule, onEdit }: { schedule: any; onEdit: (schedule: any) => void }) => {
    const { t } = useTranslation('chat');
    const navigate = useNavigate();
    const { pause, resume, runNow, remove, busy } = useScheduleActions();
    const [deleting, setDeleting] = useState(false);

    const addon = (
        <CardMenu isDisabled={busy}>
            <DropdownMenu.Item className={itemClassName} onClick={() => onEdit(schedule)}>
                <EditMenuIcon className={menuIconClassName} />
                {t('automations.menu.edit')}
            </DropdownMenu.Item>
            <DropdownMenu.Item
                className={itemClassName}
                onClick={() => navigate(url(CONVERSATION_PATH, { conversationId: schedule.conversationId }))}
            >
                <OpenChatMenuIcon className={menuIconClassName} />
                {t('automations.menu.openChat')}
            </DropdownMenu.Item>
            <DropdownMenu.Item className={itemClassName} onClick={() => runNow(schedule)}>
                <RunNowMenuIcon className={menuIconClassName} />
                {t('automations.menu.runNow')}
            </DropdownMenu.Item>
            {!schedule.isEnded && (
                <DropdownMenu.Item
                    className={itemClassName}
                    onClick={() => (schedule.active ? pause(schedule) : resume(schedule))}
                >
                    {schedule.active ? (
                        <PauseMenuIcon className={menuIconClassName} />
                    ) : (
                        <ResumeMenuIcon className={menuIconClassName} />
                    )}
                    {schedule.active ? t('automations.menu.pause') : t('automations.menu.resume')}
                </DropdownMenu.Item>
            )}
            <DropdownMenu.Separator />
            <DropdownMenu.Item className={dangerItemClassName} onClick={() => setDeleting(true)}>
                <DeleteMenuIcon className={dangerMenuIconClassName} />
                {t('automations.menu.delete')}
            </DropdownMenu.Item>
        </CardMenu>
    );

    return (
        <>
            <AssistantCardShell
                to={url(AUTOMATION_PATH, { scheduleId: schedule.id })}
                icon={<ClockIcon />}
                title={schedule.name}
                subtitle={subtitle(scheduleStatus(t, schedule), t('automations.onSchedule'))}
                addon={addon}
                meta={[
                    { icon: <ClockIcon />, label: formatCadence(t, schedule) },
                    lastRunLine(t, { at: schedule.lastRunAt, outcome: scheduleLastRun(schedule) }),
                ]}
            />
            {deleting && (
                <DeleteConfirm
                    name={schedule.name}
                    kind="schedule"
                    disabled={busy}
                    onConfirm={async () => {
                        await remove(schedule);
                        setDeleting(false);
                    }}
                    onCancel={() => setDeleting(false)}
                />
            )}
        </>
    );
};

/** A schedule someone else sends to the viewer (ENG-11387), read-only: opens the viewer's chat receiving the runs
 *  once the first one arrived; the menu opens that chat and mutes or unmutes the schedule. */
export const SharedScheduleCard = ({ schedule }: { schedule: any }) => {
    const { t } = useTranslation('chat');
    const navigate = useNavigate();
    const { publishToast } = useToaster();
    const [setMuted, { loading: muting }] = useMutation(SET_SHARED_SCHEDULE_MUTED);
    const toggleMuted = async () => {
        try {
            await setMuted({ variables: { id: schedule.id, muted: !schedule.isMuted } });
        } catch (error) {
            publishToast({ description: graphqlErrorMessage(error, t('share.muteError')), status: 'error' });
        }
    };
    const chatUrl = schedule.conversationId
        ? url(CONVERSATION_PATH, { conversationId: schedule.conversationId })
        : null;
    const sharedBy = t('automations.sharedBy', {
        name: schedule.sender ? displayName(schedule.sender) : t('projects.admin'),
    });
    const state = schedule.isMuted ? t('automations.mutedByYou') : !schedule.active ? t('automations.paused') : null;

    const addon = (
        <CardMenu isDisabled={muting}>
            {chatUrl && (
                <DropdownMenu.Item className={itemClassName} onClick={() => navigate(chatUrl)}>
                    <OpenChatMenuIcon className={menuIconClassName} />
                    {t('automations.menu.openChat')}
                </DropdownMenu.Item>
            )}
            <DropdownMenu.Item className={itemClassName} onClick={toggleMuted}>
                {schedule.isMuted ? (
                    <UnmuteMenuIcon className={menuIconClassName} />
                ) : (
                    <MuteMenuIcon className={menuIconClassName} />
                )}
                {t(schedule.isMuted ? 'automations.menu.unmute' : 'automations.menu.mute')}
            </DropdownMenu.Item>
        </CardMenu>
    );

    return (
        <AssistantCardShell
            to={chatUrl ?? undefined}
            onClick={
                chatUrl ? undefined : () => publishToast({ description: t('automations.noChatYet'), status: 'info' })
            }
            icon={<ClockIcon />}
            title={schedule.name}
            subtitle={state ? `${sharedBy} · ${state}` : sharedBy}
            muted={!schedule.active || schedule.isMuted}
            addon={addon}
            meta={[
                { icon: <ClockIcon />, label: formatCadence(t, schedule) },
                {
                    icon: <SucceededIcon />,
                    label: schedule.lastRunAt
                        ? t('automations.lastRun', { date: formatRunDate(schedule.lastRunAt) })
                        : t('automations.neverRun'),
                },
            ]}
        />
    );
};

/** An email automation (channel trigger, unchanged feature): opens today's trigger form; the menu edits,
 *  pauses/resumes or deletes it with today's trigger mutations. */
export const TriggerCard = ({
    trigger,
    canEdit,
    onEdit,
}: {
    trigger: any;
    canEdit: boolean;
    onEdit: (trigger: any) => void;
}) => {
    const { t } = useTranslation('chat');
    const { user } = useApplicationContext();
    const { publishToast } = useToaster();
    const [deleting, setDeleting] = useState(false);
    const navigate = useNavigate();
    // a personal automation (no agent) is the user's own: it runs as them, in their chat (ENG-11406)
    const personal = !trigger.agentId;
    const canManage = personal || canManageAgents(user);

    const refetch = {
        refetchQueries: [
            { query: GET_MY_TRIGGERS },
            ...(personal ? [] : [{ query: GET_AGENT_TRIGGERS, variables: { agentId: trigger.agentId } }]),
        ],
    };
    const [updateTrigger, { loading: updating }] = useMutation(UPDATE_TRIGGER, refetch);
    const [deleteTrigger, { loading: deleteLoading }] = useMutation(DELETE_TRIGGER, refetch);

    const act = async (action: () => Promise<unknown>, toastKey: string) => {
        try {
            await action();
            publishToast({ description: t(toastKey, { name: trigger.name }), status: 'success' });
        } catch (e) {
            publishToast({ description: validationErrorMessage(t, e, 'automations.toasts.error'), status: 'error' });
        }
    };

    const addon = canManage ? (
        <CardMenu isDisabled={updating || deleteLoading}>
            {canEdit && (
                <DropdownMenu.Item className={itemClassName} onClick={() => onEdit(trigger)}>
                    <EditMenuIcon className={menuIconClassName} />
                    {t('automations.menu.edit')}
                </DropdownMenu.Item>
            )}
            {personal && trigger.conversationId && (
                <DropdownMenu.Item
                    className={itemClassName}
                    onClick={() => navigate(url(CONVERSATION_PATH, { conversationId: trigger.conversationId }))}
                >
                    <OpenChatMenuIcon className={menuIconClassName} />
                    {t('automations.menu.openChat')}
                </DropdownMenu.Item>
            )}
            <DropdownMenu.Item
                className={itemClassName}
                onClick={() =>
                    act(
                        () => updateTrigger({ variables: { id: trigger.id, active: !trigger.active } }),
                        trigger.active ? 'automations.toasts.paused' : 'automations.toasts.resumed'
                    )
                }
            >
                {trigger.active ? (
                    <PauseMenuIcon className={menuIconClassName} />
                ) : (
                    <ResumeMenuIcon className={menuIconClassName} />
                )}
                {trigger.active ? t('automations.menu.pause') : t('automations.menu.resume')}
            </DropdownMenu.Item>
            <DropdownMenu.Separator />
            <DropdownMenu.Item className={dangerItemClassName} onClick={() => setDeleting(true)}>
                <DeleteMenuIcon className={dangerMenuIconClassName} />
                {t('automations.menu.delete')}
            </DropdownMenu.Item>
        </CardMenu>
    ) : null;

    return (
        <>
            <AssistantCardShell
                onClick={canManage && canEdit ? () => onEdit(trigger) : undefined}
                icon={<MailIcon />}
                title={trigger.name}
                subtitle={subtitle(
                    trigger.active ? null : { tone: 'warning', label: t('automations.paused') },
                    t('automations.onEmail')
                )}
                addon={addon}
                meta={[
                    {
                        icon: <MailIcon />,
                        label: t('automations.fromSenders', { senders: (trigger.senders || []).join(', ') }),
                    },
                    ...(personal
                        ? [
                              {
                                  icon: <UserIcon />,
                                  label: trigger.projectName
                                      ? t('trigger.runsAsYouIn', { project: trigger.projectName })
                                      : t('trigger.runsAsYou'),
                              },
                          ]
                        : []),
                    lastRunLine(t, {
                        at: trigger.lastFiredAt,
                        outcome: trigger.lastStatus === 'failed' ? 'failed' : trigger.lastFiredAt ? 'succeeded' : null,
                    }),
                ]}
            />
            {deleting && (
                <DeleteConfirm
                    name={trigger.name}
                    kind="email"
                    disabled={deleteLoading}
                    onConfirm={async () => {
                        await act(async () => {
                            const { data } = await deleteTrigger({ variables: { id: trigger.id } });
                            if (!data?.deleteTrigger?.success) throw new Error('not deleted');
                        }, 'automations.toasts.deletedEmail');
                        setDeleting(false);
                    }}
                    onCancel={() => setDeleting(false)}
                />
            )}
        </>
    );
};
